import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import { readyToCollect, type BookingPage } from '../../api/transport'
import { Button } from '../../components/Button'
import { PageBand, PageShell } from '../../components/PageShell'
import { ApiError } from '../../lib/api'
import {
  AddToCalendar,
  BookAgain,
  BookingFooter,
  ChangeOrCancel,
  ReceiptLink,
} from './BookingParts'
import { carriedFromPage, carryToNewBooking } from './carry'
import { BookingTimeline } from './BookingTimeline'
import { canAskForReturn, statusWords, whenLabel } from './bookingWords'
import { Desk } from './Desk'
import { useFocusOnMount } from './focus'
import { TransportHeader } from './TransportHeader'
import { useBookingFromLink } from './useBookingFromLink'

/**
 * The private booking page (M40-T10, spec §C.3.6): `/transport/booking/:ref`,
 * the key in the URL fragment. The timeline, Add to calendar, the receipt
 * link, change or cancel and the footer are Screen 6's parts (T09).
 *
 * "Ready to collect" shows only when T08 would accept it; the server still
 * decides, and its reason is shown if it refuses.
 */

/** Statuses for which the trip is still ahead or under way. */
const LIVE: ReadonlyArray<BookingPage['status']> = [
  'confirmed',
  'driver_assigned',
  'picked_up',
  'arrived',
  'return_requested',
  'return_picked_up',
]

export function TransportBookingPage(): ReactNode {
  const { reference = '' } = useParams()
  const { state, setPage } = useBookingFromLink(reference)
  const heading = useFocusOnMount<HTMLHeadingElement>()

  return (
    <PageShell measure={640} as="main">
      <TransportHeader />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          {state.kind === 'missing'
            ? 'Booking not found'
            : `Your booking — ${reference}`}
        </h1>
        {state.kind === 'loading' && (
          <p role="status" className="text-[13px] text-ink-3">
            Opening your booking…
          </p>
        )}
        {state.kind === 'missing' && <Missing />}
        {state.kind === 'ready' && (
          <Booking
            page={state.page}
            manageKey={state.manageKey}
            onChange={setPage}
          />
        )}
        <BookingFooter />
      </PageBand>
    </PageShell>
  )
}

/** One message for every failure (T08 cannot be told apart, nor can this). */
function Missing(): ReactNode {
  // Announced: focus is on the heading, whose words change as this arrives.
  return (
    <p role="alert" className="text-[14px] leading-relaxed text-ink-1">
      We can&apos;t find that booking. Check the link, or call the transport
      desk
      <Desk />.
    </p>
  )
}

function Booking({
  page,
  manageKey,
  onChange,
}: {
  page: BookingPage
  manageKey: string
  onChange: (page: BookingPage) => void
}): ReactNode {
  const navigate = useNavigate()
  const words = statusWords(page.status)
  const live = LIVE.includes(page.status)
  // M40-T11: the trip goes to /transport in memory only (never the URL or
  // storage); the flow starts at the safety check.
  const bookAgain = () => {
    carryToNewBooking(carriedFromPage(page))
    navigate('/transport')
  }
  return (
    <>
      {words && (
        <p
          className={`rounded-r3 px-3 py-2.5 text-[14px] font-medium ${
            page.status === 'completed'
              ? 'border border-line-2 bg-bg-soft text-ink-1'
              : 'border border-bed-warn bg-bed-warn-bg text-ink-1'
          }`}
        >
          {words}
        </p>
      )}
      <p className="text-[13px] text-ink-2">
        Pickup {whenLabel(page.pickup_at)}, {page.pickup_area} to{' '}
        {page.hospital}.
      </p>
      {live && (
        <section aria-labelledby="timeline" className="flex flex-col gap-2">
          <h2
            id="timeline"
            className="font-mono text-[10px] uppercase tracking-wide text-ink-3"
          >
            Your trip
          </h2>
          <BookingTimeline page={page} />
        </section>
      )}
      {live && page.return_mode === 'call_to_collect' && (
        <ReadyToCollect page={page} manageKey={manageKey} onChange={onChange} />
      )}
      <div className="flex flex-wrap gap-2">
        {live && <AddToCalendar page={page} />}
        <ReceiptLink reference={page.reference} manageKey={manageKey} />
      </div>
      {live && <ChangeOrCancel reference={page.reference} />}
      <BookAgain onClick={bookAgain} />
    </>
  )
}

/** setTimeout's longest wait (about 24.8 days). */
const MAX_TIMER_MS = 2_147_483_647

/** What a refused tap says: the server's reason (409), the link gone (404,
 * revoked while the page was open), or no connection. */
function tapRefusal(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return err.message
  if (err instanceof ApiError && err.status === 404)
    return "We can't find that booking any more. Call the transport desk."
  return "We couldn't reach Medcor. Try again, or call the transport desk."
}

type Tap =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'thanks' }
  | { kind: 'refused'; message: string }

function ReadyToCollect({
  page,
  manageKey,
  onChange,
}: {
  page: BookingPage
  manageKey: string
  onChange: (page: BookingPage) => void
}): ReactNode {
  const [tap, setTap] = useState<Tap>({ kind: 'idle' })
  const [now, setNow] = useState(() => new Date())
  const offered = canAskForReturn(page, now)

  // A page left open before the pickup shows the button when the pickup time
  // comes, without a reload (spec: "visible from the outbound pickup time").
  const due = Date.parse(page.pickup_at)
  const waitsForTime =
    !offered && canAskForReturn(page, new Date(Math.max(due, now.getTime())))
  useEffect(() => {
    if (!waitsForTime) return
    const wait = due - Date.now()
    if (wait > MAX_TIMER_MS) return // weeks away: a reload will show it
    const timer = setTimeout(() => setNow(new Date()), Math.max(wait, 0) + 1000)
    return () => clearTimeout(timer)
  }, [waitsForTime, due])

  const ask = () => {
    if (tap.kind === 'sending') return
    setTap({ kind: 'sending' })
    readyToCollect(page.reference, manageKey).then(
      (next) => {
        onChange(next)
        setTap({ kind: 'thanks' })
      },
      (err: unknown) =>
        setTap({
          kind: 'refused',
          message: tapRefusal(err),
        }),
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {offered && tap.kind !== 'thanks' && (
        <Button
          kind="primary"
          full
          disabled={tap.kind === 'sending'}
          onClick={ask}
        >
          Ready to collect
        </Button>
      )}
      <p
        role="status"
        className="min-h-[1.25rem] text-[13px] leading-relaxed text-ink-1"
      >
        {tap.kind === 'thanks' &&
          'Thanks — the transport desk has been told. Keep your phone on.'}
        {tap.kind === 'refused' && tap.message}
      </p>
    </div>
  )
}
